import { type RefObject, useEffect, useRef } from 'react';

export interface UseDismissOnOutsideArgs {
  open: boolean;
  refs: RefObject<HTMLElement | null>[];
  onDismiss: () => void;
}

/**
 * Closes a menu on outside `mousedown` or Escape. Shared by every
 * chartv2 popover (`Dropdown`, `ContextMenu`, `LegendMoreMenu`,
 * `ChartTypeSelector`).
 *
 * `refs` and `onDismiss` are read through a ref at event time so callers
 * can pass inline arrays / arrow functions without re-subscribing the
 * document listeners on every render.
 */
export const useDismissOnOutside = ({
  open,
  refs,
  onDismiss,
}: UseDismissOnOutsideArgs): void => {
  const latest = useRef({ refs, onDismiss });

  useEffect(() => {
    latest.current = { refs, onDismiss };
  });

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleMouseDown = (event: MouseEvent) => {
      const { target } = event;

      if (
        !(target instanceof Node) ||
        latest.current.refs.some(ref => ref.current?.contains(target))
      ) {
        return;
      }

      latest.current.onDismiss();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        latest.current.onDismiss();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);
};
